'use client';

import {
  citiesAutocomplete,
  cityDetails,
  CityAutocompleteItem,
  CityDetails,
} from '@/actions/villages';
import { useEffect, useRef, useState } from 'react';

type UseAutocompleteCitiesParams = {
  searchValue: string;
  lastSearchedQuery: string | null;
  isLoading: boolean;
  hasError: boolean;
  hasCityResults: boolean;
};

export function useAutocompleteCities({
  searchValue,
  lastSearchedQuery,
  isLoading,
  hasError,
  hasCityResults,
}: UseAutocompleteCitiesParams) {
  const [autocompleteCities, setAutocompleteCities] = useState<
    CityAutocompleteItem[]
  >([]);
  const [isAutocompleteLoading, setIsAutocompleteLoading] = useState(false);
  const [hasAutocompleteError, setHasAutocompleteError] = useState(false);
  const [hasCityDetailsError, setHasCityDetailsError] = useState(false);
  const [loadingPlaceId, setLoadingPlaceId] = useState<string | null>(null);
  const autocompleteRequestId = useRef(0);

  useEffect(() => {
    if (
      isLoading ||
      hasError ||
      hasCityResults ||
      lastSearchedQuery !== searchValue ||
      searchValue.length < 3
    ) {
      setAutocompleteCities([]);
      setIsAutocompleteLoading(false);
      setHasAutocompleteError(false);
      setHasCityDetailsError(false);
      setLoadingPlaceId(null);
      return;
    }

    const requestId = ++autocompleteRequestId.current;
    setIsAutocompleteLoading(true);
    setHasAutocompleteError(false);
    setHasCityDetailsError(false);

    citiesAutocomplete(searchValue)
      .then(({ data }) => {
        if (requestId !== autocompleteRequestId.current) return;
        setAutocompleteCities(data);
        setHasAutocompleteError(false);
      })
      .catch(() => {
        if (requestId !== autocompleteRequestId.current) return;
        setAutocompleteCities([]);
        setHasAutocompleteError(true);
      })
      .finally(() => {
        if (requestId === autocompleteRequestId.current) {
          setIsAutocompleteLoading(false);
        }
      });
  }, [
    isLoading,
    hasError,
    hasCityResults,
    lastSearchedQuery,
    searchValue,
  ]);

  const selectAutocompleteCity = async (
    city: CityAutocompleteItem,
  ): Promise<CityDetails | null> => {
    if (loadingPlaceId) return null;

    setLoadingPlaceId(city.placeId);
    setHasCityDetailsError(false);

    try {
      const { data } = await cityDetails(city.placeId, city.city);
      return data;
    } catch {
      setLoadingPlaceId(null);
      setHasCityDetailsError(true);
      return null;
    }
  };

  return {
    autocompleteCities,
    isAutocompleteLoading,
    hasCitiesError: hasAutocompleteError || hasCityDetailsError,
    loadingPlaceId,
    isCityDetailsLoading: loadingPlaceId !== null,
    selectAutocompleteCity,
  };
}
